import React, { useState } from 'react';
import styles from './spendingHistoryBarChart.module.scss';
import Loading from 'src/components/atomic/atoms/loading/Loading';
import { useSyncingAccounts } from 'src/hooks/useSyncingAccounts';
import Selectors from 'src/utils/selectors';
import { useSpendingHistory } from 'src/hooks/queries/spending';
import { LocalDate } from '@js-joda/core';
import { SpendingHistoryDataPoint } from 'src/types/spending';
import { BUDGET_OVERALL_ID } from 'src/services/budget.service';
import Highchart from 'src/components/atomic/atoms/highchart/Highchart';
import { getHistoryBarChartOptions } from 'src/components/atomic/molecules/spendingHistoryBarChart/chartConfig';
import FormatService from 'src/services/format.service';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import LiteralsService from 'src/services/literals/LiteralsService';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { getCategoryTransactionsUrl } from 'src/services/router.service';
import ChartTimePeriod from 'src/components/atomic/molecules/chartTimePeriod/ChartTimePeriod';
import H from 'history';
import DateUtils from 'src/utils/date';

export interface SpendingHistoryBarChartProps {
    title: string;
    groupId?: string;
    handleOpenTransactions: (startDate: LocalDate, endDate: LocalDate) => void;
    tagName: { Hover: string; SelectMonth: string };
    inModal?: boolean;
    propHistory?: H.History;
    categoryName?: string;
}

export default function SpendingHistoryBarChart({
    title,
    groupId,
    handleOpenTransactions,
    tagName,
    inModal,
    propHistory,
    categoryName
}: SpendingHistoryBarChartProps): JSX.Element {
    const { isLoading: isLoadingSpendingHistory, data: spendingHistory } = useSpendingHistory({
        id: groupId || BUDGET_OVERALL_ID,
        startDate: DateUtils.startDateOfThisMonthLastYear(),
        endDate: DateUtils.startDateOfThisMonth()
    });
    const { isSyncingAccounts } = useSyncingAccounts();
    const [yAxisLabelWidth, setYAxisLabelWidth] = useState<number>(0);
    const [isChartMobile, setIsChartMobile] = useState<boolean>();

    const { isCustomMinWidth: isMinWidthMobile, isMinWidthLarge: isMinWidthLaptop } = useBreakpoints(425);

    const groupSpendingHistory: SpendingHistoryDataPoint[] = spendingHistory?.[0]?.history;
    const isLoading: boolean = isSyncingAccounts || isLoadingSpendingHistory;

    const xAxisLabels: JSX.Element = (
        <div className={styles.labels} style={{ paddingLeft: `${yAxisLabelWidth + 15}px` }}>
            {groupSpendingHistory?.map((point: SpendingHistoryDataPoint, index) => {
                const startDate: LocalDate = LocalDate.parse(point.timePeriodStart);
                const endDate: LocalDate = startDate.plusMonths(1);
                return (
                    <Anchor
                        key={index}
                        data-cy={Selectors.SpendSmart.SPENDING_HISTORY_CHART_MONTHLINK}
                        className={styles.xAxisLabel}
                        aria-label={`${LiteralsService.getLiteral('Budget.Chart.viewTransactionsFor', [
                            categoryName,
                            FormatService.formatAltDateLongMonth(startDate)
                        ])}`}
                        onClick={() => handleOpenTransactions(startDate, endDate)}
                        tag={tagName.SelectMonth}
                        href={getCategoryTransactionsUrl(groupId, startDate, endDate)}
                        inline
                    >
                        {isChartMobile
                            ? FormatService.formatShortMonth(startDate)
                            : FormatService.formatMonth(startDate)}
                    </Anchor>
                );
            })}
        </div>
    );

    const chartStartDate: LocalDate = DateUtils.startDateOfThisMonthLastYear();
    const chartEndDate: LocalDate = chartStartDate.plusYears(1).withDayOfMonth(1).minusDays(1);

    return (
        <>
            <ChartTimePeriod startDate={chartStartDate} endDate={chartEndDate} propHistory={propHistory} />
            {isLoading ? (
                <Loading className={styles.syncingHistory} />
            ) : (
                <Highchart
                    title={title}
                    options={getHistoryBarChartOptions(
                        title,
                        groupSpendingHistory,
                        inModal,
                        setYAxisLabelWidth,
                        setIsChartMobile,
                        isMinWidthMobile,
                        isMinWidthLaptop,
                        categoryName
                    )}
                    tagName={tagName.Hover}
                    data-cy={Selectors.SpendSmart.SPENDING_HISTORY_CHART}
                    xAxisLabels={xAxisLabels}
                    className={styles.historyChart}
                />
            )}
        </>
    );
}
